import { useState } from 'react';
import { AlertCircle, Check, Mic, ShieldCheck, Video } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import styles from './HIPAAConsentModal.module.css';

export interface HIPAAConsentModalProps {
  open: boolean;
  /** Patient name shown in the disclosure copy */
  pacienteNombre?: string;
  /** Whether the consultation will be audio-recorded */
  audioGrabado?: boolean;
  /** Whether AI transcription / SOAP processing will run */
  iaHabilitada?: boolean;
  /** Called when the doctor accepts the consent and proceeds to the call */
  onAccept: () => void;
  /** Called when the doctor cancels (back to queue) */
  onCancel: () => void;
  /** Disabled state while the parent connects to LiveKit */
  loading?: boolean;
}

/**
 * Consent modal shown to the doctor BEFORE entering a LiveKit room.
 *
 * Required by the M4 plan (HIPAA checklist) — the doctor must acknowledge:
 *   1. The session may be audio-recorded for clinical/audit purposes
 *   2. AI may process the transcription to generate SOAP notes
 *   3. Patient PHI must be handled per Ley 81 (Panamá)
 *   4. The doctor is in a private, secure environment
 *
 * The modal blocks the call from starting until all checkboxes are ticked.
 */
export function HIPAAConsentModal({
  open,
  pacienteNombre,
  audioGrabado = true,
  iaHabilitada = true,
  onAccept,
  onCancel,
  loading = false,
}: HIPAAConsentModalProps) {
  const [ackRecording, setAckRecording] = useState(false);
  const [ackPrivacy, setAckPrivacy] = useState(false);
  const [ackEnvironment, setAckEnvironment] = useState(false);

  const allAcknowledged = ackRecording && ackPrivacy && ackEnvironment;

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title="Antes de iniciar la consulta"
    >
      <div className={styles.body}>
        {/* Header */}
        <div className={styles.intro}>
          <div className={styles.iconWrap} aria-hidden="true">
            <ShieldCheck size={22} />
          </div>
          <div>
            <h3 className={styles.title}>Aviso de privacidad y grabación</h3>
            <p className={styles.subtitle}>
              {pacienteNombre
                ? `Está a punto de iniciar una consulta con ${pacienteNombre}.`
                : 'Está a punto de iniciar una consulta clínica.'}{' '}
              Lea y confirme antes de continuar.
            </p>
          </div>
        </div>

        {/* Disclosure items */}
        <ul className={styles.disclosures}>
          {audioGrabado && (
            <li className={styles.item}>
              <Mic size={16} aria-hidden="true" />
              <div>
                <strong>Grabación de audio.</strong> La sesión se grabará
                en formato audio para fines clínicos, de auditoría y
                cumplimiento legal. La grabación se almacena cifrada por un
                mínimo de 6 años conforme a la Ley 81 de Panamá.
              </div>
            </li>
          )}

          {iaHabilitada && (
            <li className={styles.item}>
              <Video size={16} aria-hidden="true" />
              <div>
                <strong>Asistencia clínica con IA.</strong> La transcripción
                de la consulta se procesa con inteligencia artificial para
                generar notas SOAP estructuradas. Los datos del paciente no
                se utilizan para entrenamiento de modelos.
              </div>
            </li>
          )}

          <li className={styles.item}>
            <AlertCircle size={16} aria-hidden="true" />
            <div>
              <strong>Información protegida de salud (PHI).</strong> Toda la
              información clínica está sujeta a confidencialidad. No comparta
              datos del paciente fuera del sistema sin su consentimiento.
            </div>
          </li>
        </ul>

        {/* Checkboxes */}
        <fieldset className={styles.acks}>
          <legend className={styles.acksLegend}>Confirme antes de continuar:</legend>

          <label className={styles.ackRow}>
            <input
              type="checkbox"
              checked={ackRecording}
              onChange={(e) => setAckRecording(e.target.checked)}
            />
            <span>
              Entiendo que la consulta se grabará y soy responsable de
              informar al paciente.
            </span>
          </label>

          <label className={styles.ackRow}>
            <input
              type="checkbox"
              checked={ackPrivacy}
              onChange={(e) => setAckPrivacy(e.target.checked)}
            />
            <span>
              Me comprometo a mantener la confidencialidad de los datos del
              paciente según la Ley 81 de Panamá.
            </span>
          </label>

          <label className={styles.ackRow}>
            <input
              type="checkbox"
              checked={ackEnvironment}
              onChange={(e) => setAckEnvironment(e.target.checked)}
            />
            <span>
              Confirmo que estoy en un entorno privado y que no hay terceros
              presentes durante la consulta.
            </span>
          </label>
        </fieldset>

        {/* Actions */}
        <div className={styles.actions}>
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={onAccept}
            disabled={!allAcknowledged || loading}
            loading={loading}
          >
            <Check size={14} /> Acepto e iniciar consulta
          </Button>
        </div>
      </div>
    </Modal>
  );
}
